// Attachments on a question.
//
// Shows an actual thumbnail for images, because a client recognises a receipt
// by sight far faster than by "IMG_4821.HEIC" — and recognising it is how they
// know whether they already uploaded it.

import type { AttachmentSummary } from '../api/client.js';
import { api } from '../api/client.js';

export interface AttachmentListProps {
  items: AttachmentSummary[];
  onDelete: (id: string) => void;
}

export function AttachmentList({ items, onDelete }: AttachmentListProps): JSX.Element | null {
  if (items.length === 0) return null;

  return (
    <ul className="attachments">
      {items.map((item) => {
        const isImage = item.mimeType?.startsWith('image/') ?? false;
        const url = api.attachmentUrl(item.id);

        return (
          <li className="attachment" key={item.id}>
            {isImage ? (
              // Lazy so a question with fifteen receipts does not fetch them
              // all at once on a phone.
              <img className="attachment__thumb" src={url} alt="" loading="lazy" />
            ) : (
              <span className="attachment__thumb attachment__thumb--icon" aria-hidden="true">
                {item.mimeType === 'application/pdf' ? '📄' : '📁'}
              </span>
            )}

            <a className="attachment__name" href={url} target="_blank" rel="noopener noreferrer">
              {item.originalName}
            </a>

            <span className="attachment__size">{formatBytes(item.byteSize)}</span>

            <button
              type="button"
              className="attachment__remove"
              // The filename is in the label so a screen reader user knows
              // WHICH file this removes.
              aria-label={`Remove ${item.originalName}`}
              onClick={() => onDelete(item.id)}
            >
              ✕
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
